import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import InternalLinks from '@/components/seo/InternalLinks';
import SEOHead from '@/components/common/SEOHead';
import { Sparkles } from 'lucide-react';

const API_BASE = '';

export default function ProgrammaticPage({ pageType }) {
  const { slug } = useParams();
  const { currentLang } = useLanguage();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ lang: currentLang });
    if (slug) params.append('slug', slug);
    fetch(`${API_BASE}/api/seo/programmatic/${pageType}?${params}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [pageType, slug, currentLang]);

  if (loading) return <div className="container mx-auto px-4 py-8 text-slate-400">Loading...</div>;
  if (!data || data.error) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="programmatic-page">
      <SEOHead title={data.title} description={data.meta_description} />

      <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        {data.h1 || data.title}
      </h1>

      {data.intro_content && data.intro_content.length > 20 && (
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5 mb-6" data-testid="prog-intro">
          <p className="text-sm text-slate-400 leading-relaxed">{data.intro_content}</p>
        </div>
      )}

      {data.content_blocks && data.content_blocks.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {data.content_blocks.filter(b => b.content && b.content.length > 20).map((b, i) => (
            <div key={i} className="border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{b.type}</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">{b.content}</p>
            </div>
          ))}
        </div>
      )}

      <InternalLinks links={data.related_links} />
    </div>
  );
}
